/**
 * What "Erase everything stored by this site" actually sweeps, and what it is
 * then allowed to say about it.
 *
 * Lifted out of `SettingsView.tsx` unchanged (backlog:3704) for one reason:
 * the erase is now coordinated across every open tab, and the tab that
 * ANSWERS a round is usually not sitting on the Settings view. The peer
 * listener installed at boot (`eraseChannel.ts`) needs the sweep and nothing
 * else; importing it out of the React view would pull the whole Settings
 * chunk into every tab's boot path to reach three storage loops. This module
 * imports no React and no view code, so the peer half stays cheap.
 *
 * The counting discipline is the point of the whole file and did not change:
 * nothing is assumed, every area and every key is counted, and the sentence
 * the reader gets is earned by {@link eraseOutcome} from those counts.
 */
import { errorLog } from "../utils/errorLog";
import { classifyStorageError } from "../utils/storageError";
import { isOwnedStorageKey } from "../utils/storageKeys";
import { SHARE_CACHE_KEY } from "./launchIntake";

/** A key this site owns: the current prefixes plus the legacy `mi.` one. */
export function isDoomedKey(key: string): boolean {
  return isOwnedStorageKey(key) || key.startsWith("mi.");
}

/** What a sweep of the site's own keys actually managed to do. */
export interface EraseSweepResult {
  /** Owned keys the sweep could see across the areas it could read. */
  found: number;
  /** Of those, the ones confirmed gone afterwards. */
  removed: number;
  /** Storage areas the sweep read the key list out of. */
  reachable: number;
  /** Storage areas that refused to be read at all, contents unknown. */
  unreachable: number;
}

/** One area's contribution, before it is folded into a running total. */
export type AreaSweep = Omit<EraseSweepResult, "unreachable">;

/**
 * The three things this button can honestly say. `erased` is the only one
 * that claims the wipe happened, and it is earned by a count, not assumed.
 */
export type EraseOutcome = "erased" | "partial" | "unreachable";

/** An empty running total, so every caller starts from the same zero. */
export function emptySweep(): EraseSweepResult {
  return { found: 0, removed: 0, reachable: 0, unreachable: 0 };
}

/**
 * Add one area's result to a running total, counting an area that could not
 * be read at all as unreachable rather than as empty.
 *
 * Every area the sweep touches folds in through here — the two Web Storage
 * areas, Cache Storage, and the other open tabs — so "reachable 0 means
 * unreachable 1" is stated once instead of four times.
 */
export function foldArea(total: EraseSweepResult, one: AreaSweep): EraseSweepResult {
  total.found += one.found;
  total.removed += one.removed;
  total.reachable += one.reachable;
  if (one.reachable === 0) total.unreachable++;
  return total;
}

/**
 * Add a whole sweep result to a running total: the same fold as
 * {@link foldArea}, for a contributor that already knows how many areas it
 * read and how many refused (the coordinated cross-tab round, which speaks
 * for one area per tab that answered).
 */
export function foldResult(
  total: EraseSweepResult,
  r: EraseSweepResult,
): EraseSweepResult {
  total.found += r.found;
  total.removed += r.removed;
  total.reachable += r.reachable;
  total.unreachable += r.unreachable;
  return total;
}

/**
 * Sweep one storage area, counting rather than assuming.
 *
 * Two guards the single outer try/catch this replaces did not have. The key
 * list is read inside its own try, because an area that throws on
 * `storage.length` (a sandboxed iframe, a `SecurityError` origin) is not an
 * empty area: its contents stay unknown, which is not the same claim as
 * "nothing was stored". And every `removeItem` is attempted independently,
 * because a key another tab is holding (`blocked-by-other-tab`) used to abort
 * the whole list: the keys before it were gone, the keys after it were never
 * tried, and the surface said everything had been erased.
 *
 * A removal counts only when reading the key back returns null, so a
 * `removeItem` that returns without throwing and leaves the value behind is
 * not counted as a wipe either.
 */
export function sweepArea(open: () => Storage | undefined): AreaSweep {
  const miss = { found: 0, removed: 0, reachable: 0 };
  let storage: Storage | undefined;
  try {
    storage = open();
  } catch (err) {
    errorLog("eraseOwnedStorage.open", err, { kind: classifyStorageError(err).kind });
    return miss;
  }
  if (!storage) return miss;

  const doomed: string[] = [];
  try {
    // Collected before anything is removed: removing during the walk shifts
    // every later index and would skip half the list on its own.
    for (let i = 0; i < storage.length; i++) {
      const k = storage.key(i);
      if (k && isDoomedKey(k)) doomed.push(k);
    }
  } catch (err) {
    errorLog("eraseOwnedStorage.enumerate", err, {
      kind: classifyStorageError(err).kind,
    });
    return miss;
  }

  let removed = 0;
  for (const k of doomed) {
    try {
      storage.removeItem(k);
      if (storage.getItem(k) === null) removed++;
    } catch (err) {
      errorLog("eraseOwnedStorage.remove", err, {
        key: k,
        kind: classifyStorageError(err).kind,
      });
    }
  }
  return { found: doomed.length, removed, reachable: 1 };
}

/** This tab's own `sessionStorage`, the one area no other tab can reach. */
export function sweepOwnSessionStorage(): AreaSweep {
  return sweepArea(() =>
    typeof window === "undefined" ? undefined : window.sessionStorage,
  );
}

/**
 * Sweep every Cache Storage entry for a reserved Web Share Target payload.
 *
 * A share handed to this app (public/sw.js's `handleShareTarget`) stores the
 * shared bytes under {@link SHARE_CACHE_KEY} and redirects to `/?shared=1`;
 * `launchIntake.ts`'s `readSharedTarget()` is the only other code that reads
 * and deletes that entry, and it only runs when the app boots with that
 * exact query param still on the URL. A share interrupted before that boot
 * completes — the tab closed, the read throwing inside its own swallowed
 * try/catch, the app never reopened — leaves the shared message's full
 * bytes in Cache Storage with nothing left pointing at them. Before this
 * function existed, "Erase everything stored by this site" never looked
 * there at all.
 *
 * `caches.keys()` lists every cache this origin owns, not just the current
 * service-worker revision's: an old cache left behind by an SW upgrade can
 * still carry an orphaned payload, so every key is swept, not only the
 * live `CACHE_NAME`.
 *
 * Opening a cache, or matching against it, can throw before either answers
 * whether THIS cache holds the payload; that used to be indistinguishable from a
 * cache that plainly did not: both hit the same catch and the key was simply
 * skipped, so `found` never moved and `eraseOutcome` could read "erased" over
 * a cache nobody actually inspected (filed by an independent review,
 * 2026-08-28). It is counted as found-but-unresolved instead, the same shape
 * a storage key that throws on `removeItem` already gets from `sweepArea`:
 * `found` moves, `removed` does not, and the mismatch is what keeps the
 * outcome at "partial" rather than rounding an unknown up to "erased". A
 * `delete()` that throws is a different case and unchanged: `match()` already
 * confirmed the payload was there, so `found` was already counted before the
 * delete was attempted.
 *
 * Only the tab that was asked to erase sweeps this: Cache Storage is shared
 * by every tab on the origin, so a peer sweeping it too would race the
 * initiator for the same single payload and report the loser's `delete()`
 * returning false as a key it found and could not remove.
 */
export async function sweepShareCache(): Promise<AreaSweep> {
  const miss = { found: 0, removed: 0, reachable: 0 };
  if (typeof caches === "undefined") return miss;
  let keys: string[];
  try {
    keys = await caches.keys();
  } catch (err) {
    errorLog("eraseOwnedStorage.cacheKeys", err, {
      kind: classifyStorageError(err).kind,
    });
    return miss;
  }
  let found = 0;
  let removed = 0;
  for (const key of keys) {
    let cache: Cache;
    let hasPayload: boolean;
    try {
      cache = await caches.open(key);
      hasPayload = !!(await cache.match(SHARE_CACHE_KEY));
    } catch (err) {
      errorLog("eraseOwnedStorage.cacheInspect", err, {
        key,
        kind: classifyStorageError(err).kind,
      });
      found++;
      continue;
    }
    if (!hasPayload) continue;
    found++;
    try {
      if (await cache.delete(SHARE_CACHE_KEY)) removed++;
    } catch (err) {
      errorLog("eraseOwnedStorage.cacheDelete", err, {
        key,
        kind: classifyStorageError(err).kind,
      });
    }
  }
  // Reachable once the cache list itself could be read, matching sweepArea's
  // "the key list opened" line.
  return { found, removed, reachable: 1 };
}

/**
 * Remove every key this site owns from both web storage areas plus any
 * orphaned Web Share Target payload in Cache Storage, and report what that
 * came to. Exported so the outcome can be tested without a button.
 *
 * THIS TAB ONLY. `localStorage` and Cache Storage are shared by every tab on
 * the origin, so sweeping them here reaches all of them; `sessionStorage` is
 * scoped to one browsing context by the Web Storage API itself and the other
 * tabs' copies are unreachable from this call by construction. Asking them is
 * `eraseChannel.ts`'s job, and `eraseEverywhere()` there is what the button
 * calls.
 */
export async function eraseOwnedStorage(): Promise<EraseSweepResult> {
  const areas: (() => Storage | undefined)[] = [
    () => (typeof window === "undefined" ? undefined : window.localStorage),
    () => (typeof window === "undefined" ? undefined : window.sessionStorage),
  ];
  const total = emptySweep();
  for (const open of areas) foldArea(total, sweepArea(open));
  foldArea(total, await sweepShareCache());
  return total;
}

/**
 * Which of the three sentences the result earns.
 *
 * "Everything stored by this site has been erased" needs both halves: every
 * key that was found is confirmed gone, AND no area was left unread. An area
 * the sweep could not open may still hold owned keys, so a run that cleared
 * everything it could see while one area stayed shut is partial, not total.
 */
export function eraseOutcome(r: EraseSweepResult): EraseOutcome {
  if (r.reachable === 0) return "unreachable";
  if (r.removed === r.found && r.unreachable === 0) return "erased";
  return "partial";
}
